import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useBoolean } from 'react-use'

import {
  getDefaultLocale,
  getDefaultTz,
  IANA_TZ_OPTIONS,
  LANGUAGE_OPTIONS,
  LOCALE_OPTIONS,
} from '@makyoapp/makyo-common'
import { useTheme } from '@makyoapp/makyo-ui'

import { toasted } from '@/lib/toast'
import { useUpdateProfileMutation } from '@/resolvers/user/mutation'
import { UpdateUserProps } from '@/context/AuthContext'
import useAuth from '@/hooks/useAuth'
import { useReqError } from '@/hooks/useReqError'

/**
 * `useMyProfile` Hook
 * This custom hook is used to manage and update user profile within the application.
 * @returns {Object} Returns an object containing:
 * - t: A translation function provided by the i18next library.
 * - email: The email of the current user.
 * - currentName: A state variable holding the current name of the user.
 * - handleUpdateProfile: A function to update the user's profile with new data.
 * - setSelectedLocale: A function to set the selected locale state.
 * - setSelectedTz: A function to set the selected timezone state.
 * - setSelectedLang: A function to set the selected language state.
 * - setPrintLocale: A function to set the printLocale state.
 * - localeFmt: A DateTimeFormat object for the selected locale with specific styles for date and time.
 * - printLocale: A state variable indicating if the locale should be printed.
 * - selectedLang: A state variable holding the selected language.
 * - userData: A state variable holding the current user data.
 * - selectedTz: A state variable holding the selected timezone.
 * - themeState: A state variable holding the current theme state.
 * - selectedLocale: A state variable holding the selected locale.
 */
const useMyProfile = () => {
  const { userData, setLang } = useAuth()
  const { t, i18n } = useTranslation()
  const name = userData?.name?.trim()
  const email = userData?.email
  const [currentName, setCurrentName] = useState<string | undefined>(name)
  const { state: themeState, setState: setThemeState } = useTheme()
  const printAPIError = useReqError()
  const { mutate: updateProfileMutation } = useUpdateProfileMutation()

  // Find if the user's language exists in the supported LANGUAGE_OPTIONS
  const maybeSelectedLang = LANGUAGE_OPTIONS.find((o) => o.value === userData?.lang)

  const [selectedLocale, setSelectedLocale] = useState(userData?.locale || getDefaultLocale())

  // Create a DateTimeFormat object for the selected locale with specific styles for date and time
  const localeFmt = Intl.DateTimeFormat(selectedLocale, {
    dateStyle: 'short',
    timeStyle: 'medium',
  })

  const [printLocale, setPrintLocale] = useBoolean(false)

  // Initialize a state variable for selectedTz (timezone). If userData has timezone and it's included in the IANA_TZ_OPTIONS, use it, otherwise use the default timezone
  const [selectedTz, setSelectedTz] = useState(
    userData?.timezone
      ? IANA_TZ_OPTIONS.includes(userData?.timezone)
        ? userData?.timezone
        : getDefaultTz()
      : getDefaultTz()
  )

  const [selectedLang, setSelectedLang] = useState(maybeSelectedLang || LANGUAGE_OPTIONS[0])

  // Function to handle user profile update
  const handleUpdateProfile = (props: UpdateUserProps) => {
    // Copy existing user data
    const prev = { ...userData }

    // Setup new values for profile attributes. If a new value is not provided, fall back to existing value or default.
    const newName = props.name || prev.name
    const newTheme = props.theme || prev.theme
    const newLang = props.lang || prev.lang || LANGUAGE_OPTIONS[0].value
    const newTz = props.timezone || prev.timezone || IANA_TZ_OPTIONS[0]
    const newLocale = props.locale || prev.locale || LOCALE_OPTIONS[0].value

    // If a new name is provided, update the current name state
    if (props.name) {
      setCurrentName(props.name)
    }

    // If a new language is provided, update the language state and direction of the theme
    if (props.lang) {
      setLang(props.lang)
      setThemeState({
        dir: i18n.dir(props.lang),
      })
    }

    // If a new theme is provided, update the theme state
    if (props.theme) {
      setThemeState({
        mode: props.theme,
      })
    }

    // Update Profile Mutation
    updateProfileMutation(
      // mutation payload
      { newName, newTheme, newLang, newTz, newLocale },
      {
        onSuccess() {
          toasted('Profile updated!')
        },
        onError(e) {
          // Revert Changes
          setCurrentName(prev.name)
          setLang(prev.lang || LANGUAGE_OPTIONS[0].value)
          setThemeState({
            dir: i18n.dir(prev.lang),
            mode: prev.theme,
          })
          printAPIError(e)
        },
      }
    )
  }

  return {
    t,
    email,
    currentName,
    handleUpdateProfile,
    setSelectedLocale,
    setSelectedTz,
    setSelectedLang,
    setPrintLocale,
    localeFmt,
    printLocale,
    selectedLang,
    userData,
    selectedTz,
    themeState,
    selectedLocale,
  }
}

export default useMyProfile
